import { Modal } from "antd";
import axios from "axios";
import { Colors } from "../constants/Colors";

interface ShowModal {
  type?: "warning" | "error";
  title: string;
  content: React.ReactNode;
  onOk?: () => void;
}

type GlobalWithProcessEnv = typeof globalThis & {
  process?: { env?: { VITE_API_BASE_URL?: string } };
};

const resolveBaseUrl = () => {
  const viteBase = import.meta.env.VITE_API_BASE_URL;
  console.log(viteBase);
  const nodeBase = (globalThis as unknown as GlobalWithProcessEnv).process?.env
    ?.VITE_API_BASE_URL;
  return viteBase ?? nodeBase ?? "";
};

//LOCAL
export const baseURL = resolveBaseUrl();

//DEV
/* export const baseURL = "https://apidevpos.vortexpos.com";  */

//DEMO
/* export const baseURL = "https://apidemopos.vortexpos.com"; */

//PRODUCCION
/* export const baseURL = "https://api.vortexpos.com"; */

async function getAccessToken() {
  return localStorage.getItem("pos_auth_token_v1");
}

export const axiosInstance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Añadir el token en cada peticion
axiosInstance.interceptors.request.use(
  async (req) => {
    const access_token = await getAccessToken();

    if (access_token) {
      req.headers["Authorization"] = "Bearer " + access_token;
    }

    req.params = req.params || {};

    return req;
  },
  (error) => {
    return Promise.reject(error);
  },
);

function showModal({ type = "warning", title, content, onOk }: ShowModal) {
  const config = {
    title,
    content,
    keyboard: false,
    maskClosable: false,
    okButtonProps: {
      className: "!py-[10px]",
      style: {
        backgroundColor: Colors.primary,
        borderRadius: 12,
        color: "white",
      },
    },
    onOk,
  };

  if (type === "error") {
    Modal.error(config);
  } else {
    Modal.warning(config);
  }
}

let rate_limit_blocked = false;

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const error_message = error?.response?.data?.error;

    // Si el token expiró y aún no hemos reintentado esta petición
    if (
      (error_message === "token_expired" ||
        error_message === "token_invalid" ||
        error_message === "token_not_found" ||
        error_message === "unauthorized") &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        // Llamamos al endpoint de refresh
        const { data } = await axiosInstance.post("/api/auth/refresh");

        localStorage.setItem("token", data.access_token); // Guardamos el nuevo token

        // Actualizamos la petición original con el nuevo token
        originalRequest.headers["Authorization"] =
          `Bearer ${data.access_token}`;

        // Reintentamos la petición original
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.log(refreshError);

        showModal({
          title: "Sesión expirada",
          content:
            "Por seguridad, tu sesión ha expirado. Por favor, vuelve a iniciar sesión.",
        });

        /* useAuthUser.getState().logout(); */
        return Promise.reject(refreshError);
      }
    } else if (
      error_message === "Tu cuenta está desactivada. Acceso denegado."
    ) {
      showModal({
        title: "Tu cuenta está desactivada. Acceso denegado.",
        content: "Serás redirigido al login.",
        onOk: () => {
          /* useAuthUser.getState().logout(); */
        },
      });
    } else if (error?.response?.status === 500) {
      const extraMessage =
        typeof error?.response?.data?.message === "string"
          ? ` (${error?.response?.data?.message})`
          : "";

      showModal({
        type: "error",
        title: "Error del servidor",
        content: (
          <>
            Algo salió mal, intenta de nuevo o contacta a soporte.{" "}
            <a href="mailto:desarrollo@grupovortex.cl">
              desarrollo@grupovortex.cl
            </a>
            {extraMessage && (
              <>
                <br />
                <small className="text-text-secondary-500">
                  {extraMessage}
                </small>
              </>
            )}
          </>
        ),
      });
    } else if (error?.response?.status === 429) {
      if (!rate_limit_blocked) {
        rate_limit_blocked = true;
        showModal({
          type: "warning",
          title: "Límite de solicitudes alcanzado",
          content: (
            <>
              Has excedido el número máximo de solicitudes permitidas. Por
              favor, inténtalo de nuevo más tarde.
            </>
          ),
          onOk: () => {
            rate_limit_blocked = false;
          },
        });
      }
    }

    return Promise.reject(error);
  },
);
